import type { GameId, RoomCode, RoomVisibility } from '@pixelworld/shared';

/**
 * The page to load after a server update: the same game and room code. `restore` lets the
 * first player back open the room again (it was in the old server's memory).
 */
export function restoreUrl(gameId: GameId, code: RoomCode, visibility: RoomVisibility): string {
  const params = new URLSearchParams({ room: code, restore: visibility });
  return `/play/${encodeURIComponent(gameId)}?${params.toString()}`;
}

export function parseRestore(value: string | null): RoomVisibility | null {
  return value === 'public' || value === 'private' ? value : null;
}

interface WaitOptions {
  /** Answers true when the server is up. */
  isUp: () => Promise<boolean>;
  intervalMs: number;
  timeoutMs: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/** Asks until the server answers. False when it does not come back within `timeoutMs`. */
export async function waitForServer({
  isUp,
  intervalMs,
  timeoutMs,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now = Date.now,
}: WaitOptions): Promise<boolean> {
  const deadline = now() + timeoutMs;
  while (now() < deadline) {
    await sleep(intervalMs);
    try {
      if (await isUp()) return true;
    } catch {
      // Still down.
    }
  }
  return false;
}

/** The health check of the server that answers now. */
export async function serverIsUp(): Promise<boolean> {
  const response = await fetch('/api/v1/healthz', { cache: 'no-store' });
  return response.ok;
}
